/**
 * RadioPlayer — the first section.
 *
 * All playback and metadata state lives in RadioContext (mounted at App level),
 * so the stream survives route changes and the DOM here is purely presentational.
 * The v2 contract is preserved: <audio> bound to STREAM_URL, 15s metadata
 * polling, `volume`/`isMuted` semantics, play/pause and mute test ids, and the
 * cleaned track title.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRadio } from '@/lib/RadioContext';
import { LOGO_URL } from '@/lib/config';
import RadioBackdrop from './RadioBackdrop';
import PlayControl from './PlayControl';
import NowPlaying from './NowPlaying';
import VisualizerBars from './VisualizerBars';
import VolumeControl from './VolumeControl';
import SongHistory from './SongHistory';

export default function RadioPlayer() {
  const {
    isPlaying,
    isLoading,
    isOffline,
    volume,
    isMuted,
    setVolume,
    toggleMute,
    togglePlay,
    metadata,
    cleanTitle,
  } = useRadio();

  return (
    <section
      id="radio-section"
      className="dmr-section bg-[#050505]"
      aria-label="DeadMoon Radio player"
    >
      <RadioBackdrop />

      <div className="relative z-10 flex w-full max-w-4xl flex-col items-center justify-center px-2">
        {/* Station name */}
        <motion.h1
          className="dmr-heading dmr-hero mb-6 text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          DeadMoon☾Radio
        </motion.h1>

        <motion.div
          className="dmr-rule mb-8 w-24"
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        />

        <PlayControl
          isPlaying={isPlaying}
          isLoading={isLoading}
          onToggle={togglePlay}
          art={metadata.art}
          logoFallback={LOGO_URL}
        />

        <motion.div
          className="mb-6 flex w-full justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <NowPlaying
            title={cleanTitle}
            artist={metadata.artist}
            listeners={metadata.listeners}
            isOffline={isOffline}
          />
        </motion.div>

        <div className="mb-6 w-full max-w-xs">
          <VisualizerBars isPlaying={isPlaying} volume={volume} isMuted={isMuted} />
        </div>

        <motion.div
          className="mb-8 flex flex-wrap items-center justify-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <VolumeControl
            volume={volume}
            isMuted={isMuted}
            onChange={setVolume}
            onToggleMute={toggleMute}
          />
        </motion.div>

        <SongHistory history={metadata.history} />

        <motion.div
          className="flex flex-wrap items-center justify-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Link to="/about" className="dmr-button" data-testid="about-link">
            About
          </Link>
          <a
            href="#terminal-section"
            className="dmr-button dmr-button--ghost"
            data-testid="terminal-link"
          >
            Say something
          </a>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        className="pointer-events-none absolute bottom-24 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        aria-hidden="true"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="dmr-hint">scroll down</span>
          <div className="h-8 w-px bg-gradient-to-b from-[#00FF41]/50 to-transparent" />
        </div>
      </motion.div>
    </section>
  );
}
